import { UndoToastTypes, UndoToastPayload, UndoBatchType } from './types';

export const UNDO_WINDOW_MS = 45000;

export interface UndoNoun {
  singular: string;
  plural: string;
  feminine: boolean;
}

interface UndoRequest {
  method: 'post' | 'patch';
  url: string;
  body: (payload: UndoToastPayload) => Record<string, unknown>;
  restoredKey: string;
  noun: UndoNoun;
}

const BIDDINGS: UndoNoun = { singular: 'licitação', plural: 'licitações', feminine: true };
const TASKS: UndoNoun = { singular: 'tarefa', plural: 'tarefas', feminine: true };
const PROGRESSES: UndoNoun = { singular: 'andamento', plural: 'andamentos', feminine: false };
const FAVORITES: UndoNoun = { singular: 'favorito', plural: 'favoritos', feminine: false };

const removalBody = (idsKey: string) => (payload: UndoToastPayload) => ({
  [idsKey]: payload.ids,
  deleted_at: payload.deletedAt,
  companyId: payload.companyId,
  scope: payload.scope,
});

const tokenBody = (payload: UndoToastPayload) => ({
  undo_token: payload.undoToken,
  companyId: payload.companyId,
});

const oppositeMarkBody = (payload: UndoToastPayload) => ({
  notification_progress_ids: payload.ids,
  undo: true,
});

export const undoRequestByType: Record<UndoBatchType, UndoRequest> = {
  managed: {
    method: 'post',
    url: '/gerenciar_licitacoes/gerenciar/undo_batch_destroy',
    body: removalBody('bidding_ids'),
    restoredKey: 'restored_bidding_ids',
    noun: BIDDINGS,
  },
  finished: {
    method: 'post',
    url: '/gerenciar_licitacoes/gerenciar/undo_batch_destroy',
    body: removalBody('bidding_ids'),
    restoredKey: 'restored_bidding_ids',
    noun: BIDDINGS,
  },
  tasks: {
    method: 'post',
    url: '/gerenciar_licitacoes/tarefas/undo_batch_destroy',
    body: removalBody('task_ids'),
    restoredKey: 'restored_task_ids',
    noun: TASKS,
  },
  progress: {
    method: 'post',
    url: '/gerenciar_licitacoes/andamentos/undo_batch_destroy',
    body: removalBody('notification_progress_ids'),
    restoredKey: 'restored_notification_ids',
    noun: PROGRESSES,
  },
  favorites: {
    method: 'post',
    url: '/minhas_licitacoes/favorites/undo_destroy_all',
    body: removalBody('biddings'),
    restoredKey: 'restored_bidding_ids',
    noun: FAVORITES,
  },
  finish: {
    method: 'post',
    url: '/gerenciar_licitacoes/gerenciar/undo_batch_finish',
    body: tokenBody,
    restoredKey: 'restored_bidding_ids',
    noun: BIDDINGS,
  },
  reactivate: {
    method: 'post',
    url: '/gerenciar_licitacoes/gerenciar/undo_batch_reactivate',
    body: tokenBody,
    restoredKey: 'restored_bidding_ids',
    noun: BIDDINGS,
  },
  tasks_status: {
    method: 'post',
    url: '/gerenciar_licitacoes/tarefas/undo_batch_update_status',
    body: tokenBody,
    restoredKey: 'restored_task_ids',
    noun: TASKS,
  },
  progress_read: {
    method: 'patch',
    url: '/notification/new_progress/batch_mark_unread',
    body: oppositeMarkBody,
    restoredKey: 'unread_notification_progress_ids',
    noun: PROGRESSES,
  },
  progress_unread: {
    method: 'patch',
    url: '/notification/new_progress/batch_mark_read',
    body: oppositeMarkBody,
    restoredKey: 'read_notification_progress_ids',
    noun: PROGRESSES,
  },
};

interface ShowUndoToastInput {
  type: UndoBatchType;
  ids: Array<number>;
  deletedAt?: string;
  undoToken?: string;
  message: string;
  scope?: string | null;
  companyId: number;
}

export const showUndoToast = (input: ShowUndoToastInput) => ({
  type: UndoToastTypes.SHOW,
  payload: {
    ...input,
    expiresAt: Date.now() + UNDO_WINDOW_MS,
  } as UndoToastPayload,
});

export const dismissUndoToast = () => ({
  type: UndoToastTypes.DISMISS,
});

export const undoBatch = (payload: UndoToastPayload) => {
  const request = undoRequestByType[payload.type];

  return {
    types: [UndoToastTypes.UNDO_REQUEST, UndoToastTypes.UNDO_SUCCESS, UndoToastTypes.UNDO_FAILURE],
    undoType: payload.type,
    restoredKey: request.restoredKey,
    total: payload.ids.length,
    promise: (client: any) => client[request.method](request.url, request.body(payload)),
  };
};
